import { Box, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { withRequestDelay } from "../../../utilities/withRequestDelay.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { getBlankColumnFieldIds } from "../../../../../../../api/biApi.ts";
import type { ReportField } from "../../../../../../../api/types/svTypes.ts";
import { selectCurrentDataSetId } from "../../../store/selectors/metaDataSelectors.ts";
import CheckboxField from "../../common/CheckboxField.tsx";
import SwitchField from "../../common/SwitchField.tsx";
import type { BlankFieldSettings as Settings } from "./blankFieldSettings.ts";
import { getColumnOptions, toggleHideBlankFields } from "./blankFieldSettings.ts";
import ColumnsAutocomplete from "./ColumnsAutocomplete.tsx";

interface Props {
  settings: Settings;
  fields: ReportField[];
  reportType?: "Tabular" | "Pivot";
  readonlyMode: boolean;
  onChange: (settings: Settings) => void;
}

const BlankFieldSettings = ({ settings, fields, reportType = "Tabular", readonlyMode, onChange }: Props) => {
  const dataSetId = useSelector(selectCurrentDataSetId);
  const requestFields = JSON.stringify(
    fields.map(({ config }) => ({
      guid: config.guid,
      name: config.name,
      type: config.type,
      aggregation: config.aggregation,
    }))
  );
  const loadFields = useCallback(
    async (signal?: AbortSignal) => {
      try {
        if (!dataSetId) {
          throw new Error("No report data set selected");
        }
        return await getBlankColumnFieldIds(dataSetId, reportType, JSON.parse(requestFields), signal);
      } catch (error) {
        if (!signal?.aborted) {
          logError(error, "[BlankFieldSettings] load eligible columns");
        }
        throw error;
      }
    },
    [dataSetId, reportType, requestFields]
  );
  const delayedLoadFields = useMemo(() => withRequestDelay(loadFields, 250), [loadFields]);
  const [eligibleIds, loadError, { isFetching }] = useFetch(delayedLoadFields, {
    disabled: !dataSetId || !settings.hideBlank || settings.scope !== "Selected",
  });
  const options = useMemo(() => getColumnOptions(fields, eligibleIds ?? []), [fields, eligibleIds]);
  const selectedFields = useMemo(() => getColumnOptions(fields, settings.fieldIds), [fields, settings.fieldIds]);

  return (
    <Stack>
      <SwitchField
        label="Hide Blank Columns"
        checked={settings.hideBlank}
        onChange={(value) => onChange(toggleHideBlankFields(settings, value))}
        tooltipText="Hides selected configured fields when all their values are empty."
        disabled={readonlyMode}
      />
      {settings.hideBlank && (
        <Stack sx={{ ml: 1.25 }}>
          <CheckboxField
            label="Treat Zeros as Empty"
            checked={settings.hideZero}
            onChange={(value) => onChange({ ...settings, hideZero: value })}
            disabled={readonlyMode}
          />
          <RadioGroup
            aria-label="Hide blank columns scope"
            value={settings.scope}
            onChange={(_, value) => {
              if (value === "All" || value === "Selected") {
                onChange({ ...settings, scope: value });
              }
            }}
          >
            <FormControlLabel
              value="All"
              control={<Radio sx={{ p: 1 }} />}
              label="Apply for all Columns"
              disabled={readonlyMode}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              value="Selected"
              control={<Radio sx={{ p: 1 }} />}
              label="Apply for Selected Columns"
              disabled={readonlyMode}
              sx={{ m: 0 }}
            />
          </RadioGroup>
          {settings.scope === "Selected" &&
            (loadError ? (
              <DataLoadingFailed title="Could not load columns" />
            ) : (
              <Box sx={{ my: 1 }}>
                <ColumnsAutocomplete
                  options={options}
                  value={selectedFields}
                  loading={isFetching || eligibleIds === undefined}
                  disabled={readonlyMode}
                  onChange={(fieldIds) => onChange({ ...settings, fieldIds })}
                />
              </Box>
            ))}
        </Stack>
      )}
    </Stack>
  );
};

export default BlankFieldSettings;
